import { Box, Checkbox, FormControlLabel } from '@mui/material';
import { ReactElement, useCallback, useEffect } from 'react';
import {
  getFieldLabelTranslationArgs,
  useListContext,
  useStore,
  useStoreContext,
  useTranslate,
} from 'react-admin';

import { arraySymmetricDifference } from '@sh4res/bo-common/utils';

interface SelectColumnsMenuProps<T> {
  preference: string;
  defaultColumns: (keyof T)[];
  columns: Record<keyof T, ReactElement>;
  className?: string;
}

export function SelectColumnsMenu<T extends Record<string, unknown> = Record<string, unknown>>({
  preference,
  columns,
  className,
  defaultColumns,
}: SelectColumnsMenuProps<T>) {
  const { removeItem, getItem } = useStoreContext();
  const [columnsPref, setColumnsPref] = useStore(`preferences.${preference}`, defaultColumns);
  const translate = useTranslate();
  const { resource } = useListContext();

  const addColumn = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>): void => {
      setColumnsPref(
        Object.keys(columns).filter(
          (name) => name === (event.target as HTMLButtonElement).name || columnsPref.includes(name),
        ),
      );
    },
    [columnsPref, columns, setColumnsPref],
  );

  const removeColumn = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>): void => {
      // always leave at least one column
      if (columnsPref.length > 1) {
        setColumnsPref(
          columnsPref.filter((name) => name !== (event.target as HTMLButtonElement).name),
        );
      }
    },
    [columnsPref, setColumnsPref],
  );

  useEffect(
    () => () => {
      // find a better way to override unsubscribe behavior of useStore
      const item = getItem<(keyof T)[] | undefined>(`preferences.${preference}`);

      if (
        item &&
        defaultColumns.length === item.length &&
        arraySymmetricDifference(item, defaultColumns).length === 0
      ) {
        removeItem(`preferences.${preference}`);
      }
    },
    [getItem, removeItem, defaultColumns, preference],
  );

  return (
    <Box
      className={className}
      sx={(theme) => ({
        margin: `${theme.spacing(1)} ${theme.spacing(2)}`,
      })}>
      {Object.keys(columns).map((name) => (
        <Box key={name}>
          <FormControlLabel
            control={
              <Checkbox
                name={name}
                checked={columnsPref.includes(name)}
                onClick={columnsPref.includes(name) ? removeColumn : addColumn}
                data-testid="BoExternalSelectColumnsMenu_ColumnCheckbox"
              />
            }
            label={translate(
              ...(getFieldLabelTranslationArgs({
                label: columns[name].props.label,
                resource,
                source: name,
              }) as [string, Record<string, unknown>]),
            )}
          />
        </Box>
      ))}
    </Box>
  );
}
